import { useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { DocumentText1, TickCircle, Clock, WalletMinus, InfoCircle } from "iconsax-react";
import { Badge, Dropdown } from "@/shared/ui";
import { formatDateForDisplay, formatMoneyKzt } from "@/shared/utils";
import { getStatusBadgeColor } from "@/features/self/shared";
import { getActStatusLabel } from "../../../utils";
import type { GphContract, CalculationsSummary } from "../../../types";
import ContractProgressCircle from "./ContractProgressCircle";

interface Props {
  contract: GphContract;
  summary: CalculationsSummary;
  locale: "ru" | "kk" | "en";
}

export default function ContractDashboard({ contract, summary, locale }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const contractTitle =
    contract.service_items.length > 0
      ? `${t("calculations.dashboard.contractGph")} — ${contract.service_items[0].service_name}`
      : t("calculations.dashboard.contractGph");

  const serviceStartDates = contract.service_items.map((item) => item.start_date).filter(Boolean);
  const serviceEndDates = contract.service_items.map((item) => item.end_date).filter(Boolean);
  const periodStartDate =
    serviceStartDates.length > 0 ? serviceStartDates.reduce((a, b) => (a < b ? a : b)) : contract.contract_date;
  const periodEndDate =
    serviceEndDates.length > 0 ? serviceEndDates.reduce((a, b) => (a > b ? a : b)) : contract.contract_end_date;

  const cards = [
    {
      key: "paid",
      label: t("calculations.dashboard.alreadyPaid"),
      value: formatMoneyKzt(summary.totalPaid, locale),
      icon: TickCircle,
      iconColor: "text-white radial-glow background-positive-fill",
      tooltip: t("calculations.summary.paidTooltip"),
    },
    {
      key: "approved",
      label: t("calculations.dashboard.awaitingPayment"),
      value: formatMoneyKzt(summary.totalApproved, locale),
      icon: Clock,
      iconColor: "text-white radial-glow background-notice-fill",
      tooltip: t("calculations.summary.approvedTooltip"),
    },
    {
      key: "remaining",
      label: t("calculations.dashboard.contractRemainder"),
      value: formatMoneyKzt(summary.remaining, locale),
      icon: WalletMinus,
      iconColor: "text-white radial-glow background-info-fill",
      tooltip: t("calculations.summary.remainingTooltip"),
    },
  ];

  return (
    <div className="p-5 radius-3xl surface-base-fill mb-5">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 aspect-square radius-xs text-white radial-glow background-neutral-fill flex items-center justify-center">
            <DocumentText1 size={24} color="currentColor" />
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="text-body-bold-lg content-base-primary">{contractTitle}</h3>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <Badge
            variant="soft"
            color={getStatusBadgeColor(contract.status)}
            text={getActStatusLabel(contract.status, t)}
            icon={<TickCircle size={12} color="currentColor" variant="Bold" />}
          />
          <span className="text-body-regular-sm content-action-neutral">
            {formatDateForDisplay(periodStartDate)} — {formatDateForDisplay(periodEndDate)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-10 items-center">
        <div>
          <ContractProgressCircle
            paidPercent={summary.paidPercent}
            approvedPercent={summary.approvedPercent}
            contractAmount={summary.contractAmount}
            locale={locale}
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div key={card.key} className="p-5 radius-lg border surface-component-stroke flex flex-col">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "w-8 aspect-square flex justify-center items-center surface-component-fill radius-xs",
                        card.iconColor,
                      )}>
                      <Icon size={16} color="currentColor" variant="Bold" />
                    </div>
                    <p className="text-label-sm content-base-primary">{card.label}</p>
                  </div>
                  <div
                    className="relative"
                    onMouseEnter={() => setHoveredCard(i)}
                    onMouseLeave={() => setHoveredCard(null)}>
                    <Dropdown
                      open={hoveredCard === i}
                      onClose={() => setHoveredCard(null)}
                      direction="bottom"
                      align="right"
                      width="w-2xs"
                      className="elevation-level-2!">
                      <span className="content-action-neutral cursor-pointer">
                        <InfoCircle size={16} color="currentColor" />
                      </span>
                      <div
                        className="p-2 max-w-content-sm"
                        onMouseEnter={() => setHoveredCard(i)}
                        onMouseLeave={() => setHoveredCard(null)}>
                        <p className="text-label-xs content-action-neutral">{card.tooltip}</p>
                      </div>
                    </Dropdown>
                  </div>
                </div>
                <h6 className="text-display-sm content-base-primary">{card.value}</h6>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
